// Considered: rebuilding the editor's old hand-rolled preview markup (`atlas-preview-row`/
// `atlas-preview-col` in ContextViewEditorOrganism.scss) — rejected because it re-implements
// a subset of what `Node` already draws and drifts from the board (spec Reuse Audit, "New
// components — justified"). This component is a thin adapter (context provider + fabricated
// sphere + selective inertness), not a reimplementation, promoted from the
// `spike/atlas-live-preview` spike now that OQ-B (inert vs. interactive) is resolved.
import React, { useRef, useLayoutEffect } from 'react';
import { AtlasViewContext, getResponsiveBreakpoint } from '../../../organisms/AtlasComponent/AtlasViewContext';
import Node from '../Node/Node';
import AtlasHeader from '../../../atoms/atlas-header/atlas-header';
import ChipAtom from '../../../atoms/Chip/ChipAtom';
import { useSchemaType } from '../../../../hooks/useSchemaType';
import { useApiClientsOrNull } from '../../../../context/ApiClientsContext';
import { globalSphereCache } from '@center-platform/spheres/cache';
import type { AtlasViewConfig } from 'types/contextView';
import type { Sphere } from 'types/global';
import { useTranslation } from 'localization';
import './AtlasLivePreviewMolecule.scss';

/**
 * Renders the Atlas view editor's live preview using the REAL Atlas row renderer
 * instead of a hand-built mock.
 *
 * `Node` is driven by exactly two things — a `sphere` prop and `viewConfig` off
 * `AtlasViewContext` — so handing it the editor's draft config and a fabricated sphere
 * makes the preview the real renderer reacting to the real configuration. Built-in
 * toggles, molecule ordering, per-type rendering and responsive behaviour then come for
 * free and cannot disagree with the board.
 *
 * Selection vs. mutation (spec Phase 7 / OQ-B resolution): the two rows behave
 * differently on purpose.
 * - The HEADER row is interactive — clicking a cell selects that column and opens its
 *   inspector. Selection is editor-local state; nothing is mutated or navigated to.
 * - The DATA row is inert (`inert` + `aria-hidden`) — its clicks would assign
 *   perspectives or navigate to a sphere that does not exist. Assignment is a write.
 */

/**
 * A sphere shaped like the design's sample row. Deliberately carries `type: null` —
 * `useSchemaType(null)` short-circuits before building a cache key, so the preview never
 * fires a schema fetch. Naming a type that isn't already in the store would make every
 * render of this component issue a doomed request.
 */
const PREVIEW_SPHERE_ID = 'preview-sphere';

function buildPreviewSphere(name: string): Sphere {
  return {
    id: PREVIEW_SPHERE_ID,
    properties: {
      name,
      type: null,
      sphereId: 'preview-org',
      isDraft: false,
    },
    status: { currentValue: { status: 'on_track' } },
    edgeCount: 12,
    // A sample so an `ApproachBadges` column has something to render — without it the
    // data row always renders zero badges (`Node.tsx` breaks out of that case on an
    // empty array), which would desync the header/data-row cell counts for that
    // column even though a real board with a deployed approach would not.
    deployedApproaches: [
      {
        approachSystemName: 'sample_approach',
        approachName: { default: 'GTM' },
        deploymentSphereId: PREVIEW_SPHERE_ID,
        isDirect: true,
      },
    ],
    ancestors: [],
  } as unknown as Sphere;
}

/** Every callback `Node` accepts. A preview must never act, so all of them are inert. */
const NOOP = () => undefined;
const NOOP_ASYNC = async () => undefined;

/** Width descriptor `atlas-header` measures and `Node` sizes its cells from. */
type HeaderWidth = {
  label: unknown;
  width: number;
  className: string;
  perspectiveSystemName: string;
};

export interface AtlasLivePreviewMoleculeProps {
  /** The editor's live draft config — what the author is currently building. */
  viewConfig: AtlasViewConfig;
  contextSystemName?: string;
  /** Drives Node's responsive column dropping; 0 resolves to the 'full' breakpoint. */
  containerWidth?: number;
  /**
   * The context's real root sphere id, when known. Fed to `atlas-header`'s
   * `featuredSphereId` so `useAvailableTypes` resolves a real sphere instead
   * of the fabricated preview sphere id, which can never exist.
   */
  rootSphereId?: string;
  /**
   * `nodeMolecules` index of the currently-selected column, mirroring the editor's
   * column-list selection. Tints that column's header cell and data cell.
   */
  selectedColumnIndex?: number | null;
  /**
   * Called with a column's `nodeMolecules` index when its header cell is clicked.
   * The preview IS the column picker: this is how a header click reaches the editor's
   * selection state and opens that column's inspector. Omit to make the header
   * non-selectable (falls back to display-only).
   */
  onColumnSelect?: (index: number) => void;
}

const AtlasLivePreviewMolecule: React.FC<AtlasLivePreviewMoleculeProps> = ({
  viewConfig,
  contextSystemName = 'preview',
  containerWidth = 0,
  rootSphereId,
  selectedColumnIndex = null,
  onColumnSelect,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const previewSphere = React.useMemo(
    () => buildPreviewSphere(t('context.viewEditor.atlas.previewSampleName')),
    [t]
  );

  // `atlas-header` resolves each PerspectiveSelector/SphereProperty column against
  // `baseSchema.perspectives`/`.properties` (mirrors `AtlasTree`'s `baseSchemaType`,
  // driven there by the real root node's type). Without it, `baseSchema` stays null:
  // perspective columns are dropped from the header entirely and aspect labels fall back
  // to `humaniseAspectKey`, so the preview header misaligns with its data row.
  //
  // Read through `globalSphereCache`, per `/standards/frontend/api-clients.md` Rule 6.
  // This previously called `getSphereById` directly, justified on the grounds that the
  // cache was avatar-shaped and carried no `type`. That was true of `SphereInfo` but not
  // of the data: the batch endpoint already returns `type` on every item and the cache's
  // normaliser was discarding it. Surfacing it there (rather than reaching past the cache
  // here) also gives every other caller batching and dedup for free.
  const [rootSphereType, setRootSphereType] = React.useState<string | null>(null);
  React.useEffect(() => {
    let isMounted = true;
    if (rootSphereId && apiClients?.graphClient) {
      globalSphereCache
        .getSphereData(rootSphereId, apiClients.graphClient)
        .then((data) => {
          if (!isMounted) return;
          setRootSphereType(data?.info?.sphere?.properties?.type ?? null);
        })
        .catch(() => {
          // Silent fallback — baseSchema stays null, matching the pre-fix behaviour.
        });
    } else {
      setRootSphereType(null);
    }
    return () => {
      isMounted = false;
    };
  }, [rootSphereId, apiClients?.graphClient]);
  const { schema: baseSchema } = useSchemaType(rootSphereType);

  const ctx = React.useMemo(
    () => ({
      contextSystemName,
      viewConfig,
      containerWidth,
      responsiveBreakpoint: getResponsiveBreakpoint(containerWidth),
    }),
    [contextSystemName, viewConfig, containerWidth]
  );

  /**
   * The width handshake.
   *
   * `Node` sizes each molecule cell from `headerWidths`, which is not computed anywhere in
   * `Node`: `atlas-header` MEASURES its rendered cells (`offsetWidth`) and reports them
   * through `onIndividualWidthsCalculated`. In the product `AtlasTree` holds that state and
   * passes it back down. Reproducing the handshake here — measure, hold, pass down — is
   * why the header belongs in the preview regardless of the design wanting that row:
   * "the preview IS the column picker."
   */
  const [headerWidths, setHeaderWidths] = React.useState<HeaderWidth[]>([]);

  /**
   * atlas-header renames the NodeLabel cell's className from 'col-6' → 'col' when
   * NodeLabel is the only authored column (Status becomes the last header, so the
   * preceding cell gets 'col' for flex-grow). Node.tsx's lookup searches for 'col-6',
   * so without normalisation it misses the width and node-content falls back to the
   * CSS 59rem, misaligning the preview.
   *
   * This normalisation is scoped here so the real atlas board (which uses Node directly)
   * is not affected — in the board, node-content at 59rem was the pre-existing behaviour
   * for single-column views and changing it is a separate concern.
   */
  const normalizedHeaderWidths = React.useMemo(
    () =>
      headerWidths.map((h) => ({
        ...h,
        // Only NodeLabel can receive exactly 'col' (other headers get 'col-N' or
        // 'col N …'). Restoring it to 'col-6' preserves Node.tsx's lookup key.
        className: h.className === 'col' ? 'col-6' : h.className,
      })),
    [headerWidths]
  );

  /**
   * Direct DOM sync: after atlas-header measures and reports widths, query the rendered
   * header li elements for column-index 0 (ApproachBadges) and 1 (NodeLabel), sum their
   * offsetWidth, and apply that exact pixel total to node-content. This mirrors what the
   * real atlas board does after layout and accounts for the 0.72rem font-scale applied to
   * the preview container, which shifts em-based widths relative to the real board.
   */
  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const root = containerRef.current;
    if (!root || headerWidths.length === 0) return;

    const col0 = root.querySelector<HTMLElement>(
      '.atlas-headers [data-atlas-column-index="0"]'
    );
    const col1 = root.querySelector<HTMLElement>(
      '.atlas-headers [data-atlas-column-index="1"]'
    );
    if (!col0 || !col1) return;

    const totalWidth = col0.offsetWidth + col1.offsetWidth + 10;
    if (totalWidth === 0) return;

    const nodeContent = root.querySelector<HTMLElement>(
      '.atlas-live-preview__data-row .node-content'
    );
    if (nodeContent) {
      nodeContent.style.width = `${totalWidth}px`;
    }
  }, [headerWidths]);

  /**
   * The "+N hidden" badge (dev note 103 / hand-off blob 6 `NodePreview`): the current
   * breakpoint drops some columns from the header (`atlas-header`'s own responsive
   * visibility check), and this names — not merely counts — which ones. The label list
   * comes from `atlas-header` itself, since it already resolves each molecule's display
   * label (schema-driven for `PerspectiveSelector`/`SphereProperty`).
   */
  const [hiddenColumns, setHiddenColumns] = React.useState<
    { nodeMoleculeIndex: number; label: string }[]
  >([]);
  const handleHiddenColumnsCalculated = React.useCallback(
    (hidden: { nodeMoleculeIndex: number; label: string }[]) => setHiddenColumns(hidden),
    []
  );
  const hiddenColumnsLabel = t('context.viewEditor.atlas.hiddenColumnsNamed', {
    count: hiddenColumns.length,
    columns: hiddenColumns.map((h) => h.label).join(', '),
  });

  if ((viewConfig.nodeMolecules ?? []).length === 0) {
    return (
      <div className="atlas-live-preview atlas-live-preview--empty">
        {t('context.viewEditor.atlas.previewEmpty')}
      </div>
    );
  }

  return (
    <AtlasViewContext.Provider value={ctx}>
      <div className="atlas-live-preview" ref={containerRef}>
        {/*
          No label here: `ContextViewEditorOrganism` already renders the "Live preview"
          section heading above this molecule (`context-view-editor__atlas-section-label`,
          asserted by `atlasSectionsAppearInHandoffOrder`) — a second one here would
          duplicate it. This bar exists only when there's something to say.
        */}
        {hiddenColumns.length > 0 && (
          <div className="atlas-live-preview__eyebrow">
            {/*
              The visible chip stays short (+N hidden), but the NAMES are what an author
              needs — and a `title` tooltip reaches neither the keyboard nor most screen
              readers, so they are the accessible name and are also rendered as visible
              text once there is room for them.
            */}
            <ChipAtom
              className="atlas-live-preview__hidden-badge"
              label={t('context.viewEditor.atlas.hiddenColumns', { count: hiddenColumns.length })}
              aria-label={hiddenColumnsLabel}
            />
            <span className="atlas-live-preview__hidden-names">{hiddenColumnsLabel}</span>
          </div>
        )}
        <AtlasHeader
          baseSchema={baseSchema}
          filterSortState={{}}
          addOrUpdatePropertyFilter={NOOP}
          addOrUpdatePerspectiveFilter={NOOP}
          removePropertyFilterByKey={NOOP}
          addOrUpdateSortOrder={NOOP}
          removeSortOrderByKey={NOOP}
          resetFilterSortState={NOOP}
          featuredSphereId={rootSphereId ?? PREVIEW_SPHERE_ID}
          organizationId="preview-org"
          onIndividualWidthsCalculated={setHeaderWidths}
          selectedColumnIndex={selectedColumnIndex}
          onColumnSelect={onColumnSelect}
          onHiddenColumnsCalculated={handleHiddenColumnsCalculated}
        />
        {/*
          The data row is a live control surface — clicks navigate, assignment writes.
          `inert` removes it from the tab order and blocks pointer events, so it cannot
          be operated against the fabricated preview sphere, which does not exist. The header row
          above is deliberately outside this wrapper: selecting a column mutates
          nothing (spec Phase 7 / OQ-B).
        */}
        <div
          className="atlas-live-preview__data-row"
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          {...({ inert: '' } as any)}
          aria-hidden="true"
        >
          <Node
            headerWidths={normalizedHeaderWidths as never}
            sphere={previewSphere}
            level={0}
            isParentNode
            breadcrumbPath=""
            onBreadcrumbClick={NOOP}
            isSelected={false}
            onActiveSectionChange={NOOP_ASYNC}
            onEdgeCountClick={NOOP}
            onNodeClick={NOOP}
            selectedEdgeCountNode={null}
            traversalDirection="outwards"
            isVisibleGlobal
            onGlobalToggle={NOOP}
            fetchPermissionList={NOOP_ASYNC as never}
            onPerspectiveAssignmentChange={NOOP_ASYNC}
            onShowAddSphere={NOOP}
            onShowEditSphere={NOOP}
            onDeactivateNode={NOOP}
            selectedColumnIndex={selectedColumnIndex}
            tagColumnIndices
          />
        </div>
      </div>
    </AtlasViewContext.Provider>
  );
};

export default AtlasLivePreviewMolecule;
